import React, { useState, useMemo } from 'react';
import { TrendingUp } from 'lucide-react';

/* Interactive "how does my investment grow" simulator, shared between the
   Vault Cap Table page and the public /investors page. All math mirrors the
   published valuation engine: V = max(floor, U × $/user × 12 × multiple). */

const AMOUNT_STEPS = [5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 5000000, 10000000];
const DISCOUNTS = [10, 15, 20, 25];

const fmtShort = (n, cur = '$') => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000_000) return `${cur}${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${cur}${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (n >= 1_000) return `${cur}${(n / 1_000).toFixed(0)}k`;
  return `${cur}${Number(n).toLocaleString()}`;
};

export const InvestmentGrowthSlider = ({
  floor = 11_500_000,
  totalShares = 10_000_000,
  perUser = 26.49,
  multiple = 12,
  anchors = [4020, 10000, 50000, 175000, 300000, 500000, 1000000],
  currency = '$',  // valuation layer is USD, matching the signed ASA cap
  variant = 'vault',
}) => {
  const [amountIdx, setAmountIdx] = useState(3);
  const [discount, setDiscount] = useState(20);
  const [userIdx, setUserIdx] = useState(Math.min(2, anchors.length - 1));

  const amount = AMOUNT_STEPS[amountIdx];
  const users = anchors[userIdx] || anchors[anchors.length - 1] || 10000;

  const calc = useMemo(() => {
    // ASA mechanics: you convert at TODAY'S (floor) share price minus the
    // discount, then those shares ride the valuation as HiBFF scales.
    const entryPrice = floor / totalShares;
    const convPrice = entryPrice * (1 - discount / 100);
    const shares = convPrice > 0 ? Math.floor(amount / convPrice) : 0;
    const valuation = Math.max(floor, users * perUser * 12 * multiple);
    const price = valuation / totalShares;
    const value = shares * price;
    const noDiscountShares = entryPrice > 0 ? Math.floor(amount / entryPrice) : 0;
    return {
      valuation,
      price,
      entryPrice,
      convPrice,
      shares,
      value,
      multipleOnMoney: amount ? (value / amount) : 0,
      bonusShares: shares - noDiscountShares,
    };
  }, [amount, discount, users, floor, totalShares, perUser, multiple]);

  const dark = variant === 'public';

  return (
    <div
      className={`rounded-3xl p-6 lg:p-8 ${dark ? '' : 'bg-white border border-gray-200'}`}
      style={dark ? { background: '#FFFFFF', border: '1px solid rgba(15,23,42,0.12)' } : {}}
      data-testid="investment-growth-slider"
    >
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp size={18} style={{ color: '#0D5E4E' }} />
        <h3 className="text-lg font-black" style={{ color: '#0B1528' }}>See your investment grow</h3>
      </div>
      <p className="text-xs mb-6" style={{ color: '#475569' }}>
        Your investment is made under an <strong>ASA (Advance Subscription Agreement)</strong> — the UK equivalent
        of the US SAFE. Your money converts to shares at a <strong>discount</strong> to today&apos;s share price,
        and those shares ride the valuation as HiBFF scales. Drag to explore.
      </p>

      {/* Investment amount */}
      <div className="mb-5">
        <div className="flex justify-between text-xs mb-1.5" style={{ color: '#475569' }}>
          <span className="font-bold uppercase tracking-wider">Your investment</span>
          <span className="font-black text-lg tabular-nums" style={{ color: '#0B1528' }} data-testid="igs-amount-readout">{fmtShort(amount, currency)}</span>
        </div>
        <input
          type="range" min="0" max={AMOUNT_STEPS.length - 1} step="1" value={amountIdx}
          onChange={e => setAmountIdx(Number(e.target.value))}
          className="igs-range w-full cursor-pointer"
          style={{ accentColor: '#0D5E4E' }}
          aria-label="Investment amount slider"
          data-testid="igs-amount-slider"
        />
        <div className="flex justify-between text-[10px] mt-1" style={{ color: '#64748B' }}>
          <span>{fmtShort(AMOUNT_STEPS[0], currency)}</span><span>{fmtShort(AMOUNT_STEPS[AMOUNT_STEPS.length - 1], currency)}</span>
        </div>
      </div>

      {/* Discount pills */}
      <div className="mb-5">
        <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#475569' }}>Conversion discount</div>
        <div className="flex gap-2">
          {DISCOUNTS.map(d => (
            <button key={d} type="button" onClick={() => setDiscount(d)}
              className="px-4 py-1.5 rounded-full text-sm font-bold transition-all"
              style={discount === d
                ? { background: '#0D5E4E', color: 'white' }
                : { background: '#F1F5F9', color: '#475569' }}
              data-testid={`igs-discount-${d}`}>
              {d}%
            </button>
          ))}
        </div>
      </div>

      {/* Growth (users) slider */}
      <div className="mb-6">
        <div className="flex justify-between text-xs mb-1.5" style={{ color: '#475569' }}>
          <span className="font-bold uppercase tracking-wider">HiBFF grows to</span>
          <span className="font-black text-lg tabular-nums" style={{ color: '#0B1528' }} data-testid="igs-users-readout">
            {Number(users).toLocaleString()} subscribers
          </span>
        </div>
        <input
          type="range" min="0" max={Math.max(anchors.length - 1, 0)} step="1" value={userIdx}
          onChange={e => setUserIdx(Number(e.target.value))}
          className="igs-range w-full cursor-pointer"
          style={{ accentColor: '#0A2540' }}
          aria-label="Subscriber growth slider"
          data-testid="igs-users-slider"
        />
        <div className="flex justify-between text-[10px] mt-1" style={{ color: '#64748B' }}>
          <span>{Number(anchors[0] || 0).toLocaleString()}</span>
          <span>{Number(anchors[anchors.length - 1] || 0).toLocaleString()}</span>
        </div>
      </div>

      {/* Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Valuation at that scale', value: fmtShort(calc.valuation, currency), sub: `share price ${currency}${calc.price.toFixed(2)}`, testid: 'igs-valuation' },
          { label: 'You convert at', value: `${currency}${calc.convPrice.toFixed(2)}`, sub: `${discount}% below today's ${currency}${calc.entryPrice.toFixed(2)}`, testid: 'igs-conv-price' },
          { label: 'Your shares', value: calc.shares.toLocaleString(), sub: `+${Math.max(calc.bonusShares, 0).toLocaleString()} bonus vs no discount`, testid: 'igs-shares' },
          { label: 'Value at that scale', value: fmtShort(calc.value, currency), sub: `${calc.multipleOnMoney >= 10 ? calc.multipleOnMoney.toFixed(0) : calc.multipleOnMoney.toFixed(2)}x your money`, testid: 'igs-value', highlight: true },
        ].map(m => (
          <div key={m.label} className="rounded-2xl p-4"
            style={{
              background: m.highlight ? 'rgba(13,94,78,0.08)' : '#F8FAFC',
              border: m.highlight ? '1px solid rgba(13,94,78,0.35)' : '1px solid rgba(15,23,42,0.12)',
            }}
            data-testid={m.testid}>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: m.highlight ? '#0D5E4E' : '#64748B' }}>{m.label}</div>
            <div className="text-xl font-black tabular-nums" style={{ color: '#0B1528' }}>{m.value}</div>
            <div className="text-[10px] mt-1" style={{ color: '#64748B' }}>{m.sub}</div>
          </div>
        ))}
      </div>

      <p className="text-[10px] mt-4 leading-relaxed" style={{ color: '#64748B' }}>
        Deterministic illustration: conversion at {discount}% below today&apos;s share price ({fmtShort(floor, currency)} ÷ {Number(totalShares).toLocaleString()} shares);
        future value per the published formula V = max({fmtShort(floor, currency)}, U × {currency}{perUser} blended × 12 × {multiple}× — an illustrative multiple).
        Valuation figures in USD, matching the signed ASA cap; {currency}{perUser} is the blended monthly revenue per paying family (UK headline price £28/mo = $35.99 US · £1 ≈ $1.29, Oct 2026).
        Not an offer of securities. Not financial guidance.
      </p>

      <style>{`
        .igs-range { height: 10px; border-radius: 9999px; background: #E8E4DE; -webkit-appearance: none; appearance: none; }
        .igs-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 22px; height: 22px; border-radius: 50%; background: currentColor; border: 3px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,.35); cursor: grab; }
        .igs-range::-webkit-slider-thumb:active { cursor: grabbing; transform: scale(1.1); }
        .igs-range::-moz-range-thumb { width: 22px; height: 22px; border-radius: 50%; background: currentColor; border: 3px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,.35); cursor: grab; }
        .igs-range { color: #0D5E4E; }
        .igs-range[data-testid="igs-users-slider"] { color: #0A2540; }
      `}</style>
    </div>
  );
};

export default InvestmentGrowthSlider;
